import { FC, useEffect, useRef, useState } from "react";
import { Tag, TableProps, Space, Button, notification, Flex } from "antd";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { AnyObject } from "antd/lib/_util/type";
import STableLayout, {
  DEFAULT_PAGE_SIZE,
  TableLayoutRef,
} from "@/styles/STableLayout";
import useDataList from "@/hooks/useDataList";
import {
  moiList,
  applicationSubmit,
  templateGet,
} from "@/apis/reimburse";
import useFrontDelTip from "@/hooks/useFrontDelTip";
import DetailMaterialOverIssue from "@/pages/Reimburse/components/DetailMaterialOverIssue";
import { approveStatusMap } from "@/pages/Reimburse/utils/approveStatus";
import { CommonListParams } from "@/types/common";
import { ApplicationTypeEnum, ApproveStatusEnum, ReimbursRequest } from "@/types/reimburse";
import CustomTabPane from "@/components/TabPane";
import AddMaterialOverIssue from "@/pages/Reimburse/components/AddMaterialOverIssue";
import OverProductModal from "./OverProductModal";
import SText from "@/styles/SText";
import WMSAvatar from "@/styles/WMSAvatar";
import { getUserAvatarTip, getUserName } from "@/utils/user";

const MaterialOverIssueTable: FC = () => {
  const { t } = useTranslation();
  const tableRef = useRef<TableLayoutRef | null>(null);

  const [list, total, loading, { fetchList, updateItem, addItem }] = useDataList<
    ReimbursRequest,
    [CommonListParams | undefined]
  >({
    fetchApi: moiList,
  });

  const [detail, setDetail] = useState<ReimbursRequest | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [templateChecking, setTemplateChecking] = useState(false);
  const [openProduct, setOpenProduct] = useState<string>('');

  const fetch = (offset: number) => {
    const take = tableRef.current?.pageSize ?? DEFAULT_PAGE_SIZE;

    fetchList({
      pagination: { offset, take },
    });
  };

  useEffect(() => {
    fetch(0);
  }, []);

  const onDetail = (record: ReimbursRequest) => setDetail(record);

  const handleOpenAdd = async () => {
    setTemplateChecking(true);
    try {
      await templateGet(ApplicationTypeEnum.MATERIAL_OVER_ISSUE);
      setAddOpen(true);
    } catch (error) {
      console.error("Approve template check error:", error);
      notification.error({
        placement: "top",
        message: t("common.tip"),
        description: `请先配置${t("layout.approveTemplate")}`,
      });
    } finally {
      setTemplateChecking(false);
    }
  };

  const { onConfirm: onSubmit } = useFrontDelTip({
    cb: (reSubmit: boolean, id: string) =>
      applicationSubmit(reSubmit, id)
        .then((res) => {
          notification.success({
            placement: "top",
            message: t("reimburse.submitSuccess"),
          });
          updateItem(res);
        })
        .catch(() => {
          notification.error({
            placement: "top",
            message: t("reimburse.submitFailed"),
          });
        }),
    message: t("common.tip"),
    desc: t("reimburse.submitOverIssueConfirm"),
  });

  const columns: TableProps<ReimbursRequest>["columns"] = [
    {
      title: t("reimburse.number"),
      dataIndex: "number",
      key: "number",
      width: 200,
      ellipsis: true,
      sorter: (a, b) => (a.number ?? "").localeCompare(b.number ?? ""),
    },
    {
      title: t("reimburse.title"),
      dataIndex: "title",
      key: "title",
      width: 220,
      ellipsis: true,
    },
    {
      title: t("reimburse.content"),
      dataIndex: "content",
      key: "content",
      width: 260,
      ellipsis: true,
    },
    {
      title: t("reimburse.status"),
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status: ApproveStatusEnum) => {
        const data = approveStatusMap[status];

        return <Tag color={data.color}>{t(data.label)}</Tag>;
      },
    },
    {
      title: t("common.createTime"),
      dataIndex: "createdAt",
      key: "createdAt",
      width: 180,
      sorter: (a, b) => (a.createdAt ?? 0) - (b.createdAt ?? 0),
      render: (time: number) =>
        time ? dayjs(time).format("YYYY-MM-DD HH:mm:ss") : "-",
    },
    {
      title: t("reimburse.submitTime"),
      dataIndex: "submitAt",
      key: "submitAt",
      width: 180,
      sorter: (a, b) => (a.submitAt ?? 0) - (b.submitAt ?? 0),
      render: (time: number | null) =>
        time ? dayjs(time).format("YYYY-MM-DD HH:mm:ss") : "-",
    },
    {
      title: t("reimburse.completeTime"),
      dataIndex: "completeAt",
      key: "completeAt",
      width: 180,
      sorter: (a, b) => (a.completeAt ?? 0) - (b.completeAt ?? 0),
      render: (time: number | null) =>
        time ? dayjs(time).format("YYYY-MM-DD HH:mm:ss") : "-",
    },
    {
      title: <SText text={t("common.creator")} />,
      dataIndex: "creator",
      width: 100,
      render: (_, item) => {
        let userData = item.creator;
        return (
          userData && <Flex>
            <div>
              <WMSAvatar creator={userData} uniChar={userData.id}
                avatarId={userData.avatar}
                size={24} alt={getUserAvatarTip(userData)}
              />
            </div>
            <SText text={getUserName(userData)} />
          </Flex>
        )
      },
    },
    {
      title: t("common.opt"),
      fixed: "right",
      key: "opt",
      width: 180,
      render: (_, record) => (
        <Space>
          <Button type="primary" onClick={() => onDetail(record)}>
            {t("common.detail")}
          </Button>
          {
            record.status === ApproveStatusEnum.DRAFT && (
              <Button type="link" onClick={() => onSubmit(false, record.id)}>
                {t("reimburse.submit")}
              </Button>
            )}
          {
            record.status === ApproveStatusEnum.RETURNED && (
              <Button type="link" onClick={() => onSubmit(false, record.id)}>
                {t("reimburse.reSubmit")}
              </Button>
            )}
          {
            record.type === ApplicationTypeEnum.MATERIAL_OVER_ISSUE &&
            record.status === ApproveStatusEnum.APPROVED && (
              <Button type="link" onClick={() => setOpenProduct(record.id)}>
                {t("requirement.createPreOutInventory")}
              </Button>
            )}
        </Space>
      ),
    },

  ];

  const headerChildren = (
    <Button
      type="primary"
      loading={templateChecking}
      disabled={templateChecking}
      onClick={handleOpenAdd}
    >
      {t("reimburse.addMaterialOverIssue")}
    </Button>
  );

  return (
    <>
      <STableLayout
        loading={loading}
        dataSource={list}
        total={total}
        columns={columns as Array<AnyObject>}
        rowKey="id"
        headerChildren={headerChildren}
        tableLayoutRef={tableRef}
        handleChange={fetch}
        scrollX={1340}
      />
      {addOpen && (
        <AddMaterialOverIssue
          open={addOpen}
          onClose={() => setAddOpen(false)}
          onSuccess={(data) => {
            addItem(data);
            setAddOpen(false);
          }}
        />
      )}
      {detail && (
        <DetailMaterialOverIssue
          detail={detail}
          open={!!detail}
          editable
          onClose={() => setDetail(null)}
          onSuccess={(data) => {
            updateItem(data);
            setDetail(null);
          }}
        />
      )}
      {
        !!openProduct && <OverProductModal open={!!openProduct} moiReimburseId={openProduct} onClose={() => setOpenProduct('')} onSucc={() => setOpenProduct('')} />
      }
    </>
  );
};

const MaterialOverIssueList: FC = () => {
  const { t } = useTranslation();

  return (
    <CustomTabPane<ReimbursRequest>
      defaultKey="list"
      defaultTabs={[
        {
          key: "list",
          label: t("reimburse.materialOverIssueList"),
          children: <MaterialOverIssueTable />,
          closable: false,
        },
      ]}
      onOpens={() => ({})}
    />
  );
};

export default MaterialOverIssueList;
